import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { setHideWelcomeTour } from '../utils/supabaseDataManager';
import './WelcomeTour.css';

function WelcomeTour({ onComplete }) {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      title: '👋 Welcome to Functional Strength!',
      description: 'Your AI-powered personal training companion',
      content: (
        <div className="tour-content">
          <p className="tour-text">
            This app is designed to help you build strength, track progress, and reach your fitness goals with personalized AI workouts and nutrition plans.
          </p>
          <p className="tour-text">
            Let's take a quick tour to show you how everything works!
          </p>
        </div>
      ),
      image: '💪'
    },
    {
      title: '📋 Week 1: Assessment Week',
      description: 'Your journey starts here',
      content: (
        <div className="tour-content">
          <p className="tour-text">
            <strong>Every new user starts with an Assessment Week</strong> based on your training goals:
          </p>
          <ul className="tour-list">
            <li>🎯 Initial workouts designed for your fitness level</li>
            <li>📊 Log performance data (weights, reps, sets)</li>
            <li>⚖️ Track body measurements (weight, body fat, pull-ups, etc.)</li>
            <li>🧠 AI learns your baseline capabilities</li>
          </ul>
          <p className="tour-text">
            At the end of Week 1, the AI uses this data to create your <strong>personalized Week 2 plan</strong>!
          </p>
        </div>
      ),
      image: '📋'
    },
    {
      title: '🏠 Home Screen',
      description: 'Your daily workout hub',
      content: (
        <div className="tour-content">
          <p className="tour-text">
            <strong>Today's Workout Card</strong> shows your scheduled workout with:
          </p>
          <ul className="tour-list">
            <li>🎯 Focus area and coaching insights</li>
            <li>🍽️ Nutrition goals (tap to track meals)</li>
            <li>💪 Exercise count</li>
            <li>🏋️ Start button to begin</li>
          </ul>
          <p className="tour-text">
            You can also switch to a home workout alternative or skip the day if needed.
          </p>
        </div>
      ),
      image: '🏠'
    },
    {
      title: '🧠 AI Weekly Planner',
      description: 'Personalized workout & nutrition plans',
      content: (
        <div className="tour-content">
          <p className="tour-text">
            <strong>Every week</strong>, Claude AI analyzes your data to create a complete plan:
          </p>
          <ul className="tour-list">
            <li>🏋️ Custom workout routines with progressive overload</li>
            <li>🍽️ Daily calorie and protein goals tailored to your progress</li>
            <li>📊 Based on your workout performance data</li>
            <li>⚖️ Based on your body measurements and changes</li>
            <li>🎯 Aligned with your specific fitness goals</li>
          </ul>
          <p className="tour-text">
            The AI continuously adapts as you improve!
          </p>
        </div>
      ),
      image: '🧠'
    },
    {
      title: '💪 During Workouts',
      description: 'Track your performance',
      content: (
        <div className="tour-content">
          <p className="tour-text">
            <strong>Set-by-Set Tracking:</strong>
          </p>
          <ul className="tour-list">
            <li>✅ Log weight and reps for each set</li>
            <li>📝 See your last workout's performance</li>
            <li>🎯 Get real-time coaching tips</li>
            <li>⏭️ Skip exercises if needed</li>
          </ul>
          <p className="tour-text">
            Your data helps the AI plan better workouts each week!
          </p>
        </div>
      ),
      image: '🏋️'
    },
    {
      title: '🍽️ Nutrition Tracker',
      description: 'Track calories & protein intake',
      content: (
        <div className="tour-content">
          <p className="tour-text">
            <strong>Track your daily calorie and protein intake</strong> to reach your goals:
          </p>
          <ul className="tour-list">
            <li>🤖 AI meal estimator - just describe what you ate!</li>
            <li>📏 Auto-calculates serving sizes (oz for solids, cups for liquids)</li>
            <li>🍔 Split complex meals into separate items (burger + fries)</li>
            <li>📊 Visual progress bars show daily progress</li>
            <li>💾 Save frequent foods for quick logging</li>
          </ul>
          <p className="tour-text">
            The AI uses your nutrition data to refine weekly goals!
          </p>
        </div>
      ),
      image: '🍽️'
    },
    {
      title: '📊 Progress Tracking',
      description: 'Watch yourself improve',
      content: (
        <div className="tour-content">
          <p className="tour-text">
            <strong>Track Multiple Metrics:</strong>
          </p>
          <ul className="tour-list">
            <li>💪 Strength gains (weight & reps)</li>
            <li>⚖️ Body weight changes</li>
            <li>📉 Body fat percentage</li>
            <li>🎯 Pull-ups, push-ups, and more</li>
          </ul>
          <p className="tour-text">
            Update measurements weekly for best AI recommendations!
          </p>
        </div>
      ),
      image: '📈'
    },
    {
      title: '📅 Weekly Review & AI Planning',
      description: 'Your weekly progression cycle',
      content: (
        <div className="tour-content">
          <p className="tour-text">
            <strong>Every Sunday, complete your Weekly Review:</strong>
          </p>
          <ul className="tour-list">
            <li>⚖️ Update body measurements (weight, body fat, pull-ups, etc.)</li>
            <li>📊 AI analyzes all workout performance from the week</li>
            <li>🍽️ AI reviews your nutrition tracking compliance</li>
            <li>🧠 AI generates customized workout AND nutrition plan</li>
            <li>🚀 Get optimized calorie/protein goals for next week</li>
          </ul>
          <p className="tour-text">
            <strong>Complete data = better AI recommendations!</strong>
          </p>
        </div>
      ),
      image: '📅'
    },
    {
      title: '🔐 Multi-User Security',
      description: 'Share the app safely',
      content: (
        <div className="tour-content">
          <p className="tour-text">
            <strong>Your Data is Protected:</strong>
          </p>
          <ul className="tour-list">
            <li>🔑 Each user has a 4-digit passcode</li>
            <li>👥 Multiple family members can use the app</li>
            <li>☁️ All data syncs across your devices</li>
            <li>🔄 Switch users anytime from the Home screen</li>
          </ul>
          <p className="tour-text">
            Change your passcode in Settings if needed!
          </p>
        </div>
      ),
      image: '🔐'
    },
    {
      title: '🚀 You\'re All Set!',
      description: 'Time to start your fitness journey',
      content: (
        <div className="tour-content">
          <p className="tour-text">
            <strong>Your Journey:</strong>
          </p>
          <ul className="tour-list">
            <li>📋 Week 1: Complete assessment workouts & track measurements</li>
            <li>📊 End of Week 1: Weekly Review with AI plan generation</li>
            <li>🚀 Week 2+: Follow AI-personalized workout & nutrition plans</li>
            <li>🔄 Every Sunday: Update data, get new AI-generated plan</li>
            <li>📈 The more you track, the smarter the AI becomes!</li>
          </ul>
          <p className="tour-text">
            Ready to build the body you deserve? Let's go! 💪
          </p>
        </div>
      ),
      image: '🎉'
    }
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleFinish();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleDoNotShowAgain = async () => {
    // Save to database
    await setHideWelcomeTour(true);

    // Also save to localStorage for backwards compatibility
    const username = localStorage.getItem('currentUser');
    if (username) {
      localStorage.setItem(`${username}_tourCompleted`, 'true');
    }

    if (onComplete) {
      onComplete();
    } else {
      navigate('/');
    }
  };

  const handleFinish = () => {
    // Mark tour as completed in localStorage (for this session only)
    const username = localStorage.getItem('currentUser');
    if (username) {
      localStorage.setItem(`${username}_tourCompleted`, 'true');
    }

    if (onComplete) {
      onComplete();
    } else {
      navigate('/');
    }
  };

  const step = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;

  return (
    <div className="welcome-tour-overlay">
      <div className="welcome-tour-modal">
        <div className="tour-header">
          <div className="tour-icon">{step.image}</div>
          <h2 className="tour-title">{step.title}</h2>
          <p className="tour-description">{step.description}</p>
        </div>

        <div className="tour-body">
          {step.content}
        </div>

        <div className="tour-progress">
          <div className="tour-dots">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`tour-dot ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
                onClick={() => setCurrentStep(index)}
              />
            ))}
          </div>
          <div className="tour-step-counter">
            Step {currentStep + 1} of {steps.length}
          </div>
        </div>

        <div className="tour-actions">
          {currentStep > 0 && (
            <button className="tour-btn tour-btn-secondary" onClick={handlePrevious}>
              ← Previous
            </button>
          )}

          <button className="tour-btn tour-btn-skip" onClick={handleDoNotShowAgain}>
            Do Not Show Again
          </button>

          <button className="tour-btn tour-btn-primary" onClick={handleNext}>
            {isLastStep ? 'Get Started! 🚀' : 'Next →'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default WelcomeTour;
